// reverence for this technique https://www.youtube.com/watch?v=ZKlXqrcBx88
// this compeont use the sliced context pattern but is tightly coupled with
// localstorage and specific store type related to the Global app context
// This context is used globaly on the app and not recommended to use it
// anywhere else instead use the Agnositic version in createSliceContext.tsx

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getIsDemoFarm } from 'utils/general';

import { IstoreData } from '@/shared/hooks/context/createGenericContext/context';

const storeLocalStorage =
  typeof window !== 'undefined' ? localStorage.getItem('storeData') : undefined;

// NOTE: we clear localstorage every logout to avoid issue when user login by another company
// this function called when logout in context auth
export const clearStoreData = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('storeData');
    sessionStorage.clear();
  }
};

// NOTE: only asset is saved in local storage as whitelist
const initialState =
  storeLocalStorage && storeLocalStorage !== 'undefined'
    ? JSON.parse(storeLocalStorage)
    : undefined;

// list of state that should be saved in localstorage
// isDemoFarm is stored to be accessed in axios interceptor
const whiteList = ['asset', 'assetsList', 'isLoadingPage', 'isDemoFarm'];

export default function createGenericContext() {
  function useStoreData(): {
    get: () => IstoreData;
    set: (value: Partial<IstoreData> | null) => void;
    subscribe: (callback: () => void) => () => void;
  } {
    const store = useRef(initialState);

    // get current data
    const get = useCallback(() => store.current, []);

    //
    const subscribers = useRef(new Set<() => void>());

    // set data and save to localstorage
    // and call subscribers for rerender (every time u use useStore create subscriber)

    const set = useCallback((value: Partial<IstoreData> | null) => {
      // if value null clear context and local storage
      if (value === null) {
        store.current = undefined;
        typeof window !== 'undefined'
          ? localStorage.removeItem('storeData')
          : undefined;
        return;
      }

      const updatedStore: IstoreData = { ...store.current, ...value };

      /***************** set demo farm flag ****************/
      if (updatedStore?.asset?.id)
        updatedStore.isDemoFarm = getIsDemoFarm(updatedStore.asset);
      store.current = updatedStore;

      const filteredStore = Object.keys(store.current)
        .filter((key) => whiteList.includes(key))
        .reduce((acc, key) => {
          return {
            ...acc,
            [key]: store.current[key],
          };
        }, {});

      subscribers.current.forEach((callback) => callback());
      // shoud save to localstorage or not
      if (whiteList.includes(Object.keys(value)[0]))
        localStorage.setItem(
          'storeData',
          JSON.stringify({ ...filteredStore, ...value })
        );
    }, []);

    // add subscriber and unsubscribe (cleaner)
    const subscribe = useCallback((callback: () => void) => {
      subscribers.current.add(callback);
      return () => subscribers.current.delete(callback);
    }, []);

    return {
      get,
      set,
      subscribe,
    };
  }

  function GenericContextProvider({ children }: { children: React.ReactNode }) {
    return (
      <StoreContext.Provider value={useStoreData()}>
        {children}
      </StoreContext.Provider>
    );
  }

  return {
    GenericContextProvider,
  };
}

type UseStoreDataReturnType = {
  get: () => IstoreData;
  set: (value: Partial<IstoreData> | null) => void;
  subscribe: (callback: () => void) => () => void;
};
export const StoreContext = createContext<UseStoreDataReturnType | null>(null);

export function useStore<SelectorOutput>(
  selector: (store: IstoreData) => SelectorOutput
): [SelectorOutput, (value: Partial<IstoreData> | null) => void] {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('Store not found');
  }
  // every change in (selected) state will call subscribe with callback selectors for rerender
  const [state, setState] = useState(() => selector(store?.get()));
  useEffect(() => {
    return store.subscribe(() => setState(selector(store?.get())));
  }, [selector, store]);
  // const state = useSyncExternalStore(store.subscribe, () =>
  //   selector(store.get())
  // );

  return [state, store.set];
}

export const { GenericContextProvider } = createGenericContext();
